import Tag from '@cashew/tag';
import cn from 'classnames';
import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import Excel from '../../assets/Excel';
import Pdf from '../../assets/Pdf';
import type { RootState } from '../../reducers';
import { setSelectedMerchants } from '../../reducers/general';

const { priceFormat, dateFormat, api } = require('@cashew/utils');

const ns = 'settlement-item';

interface SettlementData {
  settlementId: string | number;
  cycleEndDate: string;
  cycleStartDate: string;
  processedDate?: string;
  status: string;
  balance?: number;
  currencyCode?: string;
  isBalance?: boolean;
  financePartnerId?: string | number;
  financePartnerName?: string;
  settlementType: string;
}

interface Props {
  data: SettlementData;
}

export default ({
  data: {
    settlementId,
    cycleEndDate,
    cycleStartDate,
    processedDate,
    status,
    balance = 0,
    currencyCode = 'AED',
    isBalance = true,
    financePartnerId,
    financePartnerName,
    settlementType,
  },
}: Props) => {
  const { t } = useTranslation();
  const [isDownloading, setIsDownloading] = useState(false);

  const dispatch = useDispatch();
  const selectedMerchants = useSelector(
    (state: RootState) => state.general.selectedMerchants
  );

  const isSelected = selectedMerchants.includes(settlementId);

  return (
			<div className={ns}>
				<div>
					<input
						type="checkbox"
						className="form-checkbox rounded h-4 w-4 bg-black text-black accent-black"
						checked={isSelected}
						disabled={status === 'PROCESSED'}
						onChange={() => {
							let updated;
							if (!isSelected) {
								updated = [...selectedMerchants, settlementId];
							} else {
								updated = selectedMerchants.filter((id) => id !== settlementId);
							}
							dispatch(setSelectedMerchants(updated));
						}}
					/>
				</div>
				<div className="flex justify-content-start">{settlementId}</div>
				<div>
					<span className="d-block">
						{dateFormat({ date: cycleStartDate })} -{' '}
						{dateFormat({ date: cycleEndDate })}
					</span>
					{processedDate && (
						<span className="d-block">
							({dateFormat({ date: processedDate })})
						</span>
					)}
				</div>

				{settlementType !== 'BANK_CASHEW' &&
					settlementType !== 'PAYFORT_CASHEW' && (
						<div className="flex justify-content-center align-items-center flex-column">
							<span className="d-block three-dots">{financePartnerId}</span>
							<span>-</span>
							<span className="d-block three-dots">{financePartnerName}</span>
						</div>
					)}

				<div className="flex flex-column justify-content-end">
					<span
						className={cn(`${ns}--dark`, `${ns}__amount`, {
							[`${ns}--partner-positive`]: isBalance && balance > 0,
							[`${ns}--partner-negative`]: isBalance && balance < 0,
						})}
					>
						{priceFormat({
							currency: currencyCode,
							amount: balance * -1,
							isBalance,
						})}
					</span>

					<div>
						<Tag status={status}>
							<>{t(`status.${status}`)}</>
						</Tag>
					</div>
				</div>
				{settlementType !== 'MERCHANT_CASHEW' &&
					settlementType !== 'PAYFORT_CASHEW' && (
						<div
							className={`downloads ${isDownloading ? 'is-downloading' : ''}`}
						>
							<div className="loading" />
							<ul>
								<li>
									<button
										type="button"
										onClick={(event) => {
											event.preventDefault();
											if (!isDownloading) {
												setIsDownloading(true);
												api
													.get({
														endpoint: `settlements/${settlementId}/excel`,
													})
													.then((response: any) => {
														const a = document.createElement('a');
														a.href = response.data.downloadUrl;
														a.download = `${settlementId}.xlsx`;
														document.body.appendChild(a);
														a.click();
														setIsDownloading(false);
													})
													.catch((error: any) => {
														console.log(error);
													});
											}
										}}
									>
										<Excel />
									</button>
								</li>
								<li>
									<button
										type="button"
										onClick={(event) => {
											event.preventDefault();
											if (!isDownloading) {
												setIsDownloading(true);
												api
													.get({
														endpoint: `settlements/${settlementId}/pdf`,
													})
													.then((response: any) => {
														const a = document.createElement('a');
														a.target = '_blank';
														a.href = response.data.downloadUrl;
														a.download = `${settlementId}.pdf`;
														document.body.appendChild(a);
														a.click();
														setIsDownloading(false);
													})
													.catch((error: any) => {
														console.log(error);
													});
											}
										}}
									>
										<Pdf />
									</button>
								</li>
							</ul>
						</div>
					)}
			</div>
		);
};
